import moment from 'moment';
import React from 'react';
import { Api } from 'services';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { mobileRegex, emailRegex, BATCH_COLORS, COURSE_BADGE_TOKENS } from './constants';
import { splitPhone, isIndianPhone } from 'components/phoneCountryUtils';

import ImageIcon from '../dashboardV3/image.svg';
import PdfIcon from '../dashboardV3/PDF.svg';
import DocIcon from '../dashboardV3/DOC.svg';
import VideoCourseIconSvg from '../dashboardV3/video-course-icon.svg';
import StudyMaterialsIconSvg from '../dashboardV3/study-materials-icon.svg';
import TestSeriesIconSvg from '../dashboardV3/test-series-icon.svg';
import LiveCoursesIconSvg from '../dashboardV3/live-courses-icon.svg';

export const resolveStaffImageUrl = (image) => {
    if (!image || image === "default.png") return "";
    if (image.startsWith("http") || image.startsWith("blob:")) return image;
    return `${Api._s3_url}${image}`;
};

export const mobileDigitsOf = (value) => {
    const v = value || "";
    if (v.startsWith('+91')) return v.slice(3).replace(/\D/g, "");
    if (v.startsWith('91')) return v.slice(2).replace(/\D/g, "");
    // Other countries: the digits typed after their calling code.
    if (v.startsWith('+')) return splitPhone(v).digits;
    return v.replace(/\D/g, "");
};

// Only Indian (+91) numbers are format-validated; for any other country a
// number just needs to be entered.
export const isMobileValid = (value) => {
    const digits = mobileDigitsOf(value);
    if (!digits) return true;
    return isIndianPhone(value) ? mobileRegex.test(digits) : true;
};

// A complete number has been entered: 10-digit valid Indian mobile, or any
// digits for another country.
export const isMobileComplete = (value) => {
    if (!value || value === 'NA') return false;
    const digits = mobileDigitsOf(value);
    if (!digits) return false;
    return isIndianPhone(value) ? (digits.length === 10 && mobileRegex.test(digits)) : true;
};
export const isEmailValid = (value) => !value || emailRegex.test(value.trim());

export const formatDob = (d) => {
    if (!d) return "";
    const m = moment(d);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const formatPhoneNumber = (phone) => {
    if (!phone || phone === "NA") return "NA";
    const str = String(phone).trim();
    if (!str || str === "+91" || str === "91") return "NA";
    if (str.startsWith("+91")) {
        const digits = str.slice(3).replace(/\s+/g, '');
        return digits ? `+91 ${digits}` : "NA";
    }
    if (str.startsWith("91") && str.length === 12) {
        const digits = str.slice(2).replace(/\s+/g, '');
        return digits ? `+91 ${digits}` : "NA";
    }
    if (/^\d{10}$/.test(str)) {
        return `+91 ${str}`;
    }
    const match = str.match(/^(\+\d{1,4})(.*)$/);
    if (match) {
        const code = match[1];
        const rest = match[2].replace(/\s+/g, '');
        return rest ? `${code} ${rest}` : "NA";
    }
    return str;
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const WORD_EXTENSIONS = ["doc", "docx"];

export const getDocumentExtension = (doc) => {
    // The file name / S3 path extension is the most reliable signal; the type
    // fields can come back as MIME types ("image/png") or generic labels.
    const source = doc?.fileName || doc?.name || doc?.s3Path || "";
    const fromName = source.includes(".") ? source.split(".").pop().toLowerCase() : "";
    if (fromName) return fromName;
    const fromType = (doc?.fileType || doc?.type || doc?.mimeType || "").toLowerCase();
    // "image/png" -> "png", "application/pdf" -> "pdf", ".png" -> "png"
    return fromType.split("/").pop().replace(".", "");
};

export const resolveDocumentUrl = (s3Path) => {
    if (!s3Path) return "";
    if (s3Path.startsWith("http") || s3Path.startsWith("blob:")) return s3Path;
    return `${Api._s3_url}${s3Path}`;
};

export const mapDocumentRow = (doc, idx) => {
    const extension = getDocumentExtension(doc);
    const isImage = IMAGE_EXTENSIONS.includes(extension);
    const isWord = WORD_EXTENSIONS.includes(extension);
    const sizeBytes = doc?.fileSize ?? doc?.size;
    // Under 1 MB shows KB, otherwise MB.
    const sizeLabel = !sizeBytes ? ""
        : sizeBytes < 1024 * 1024 ? `${(sizeBytes / 1024).toFixed(2)} KB`
        : `${(sizeBytes / 1024 / 1024).toFixed(2)} MB`;
    const dateValue = doc?.uploadedAt || doc?.createdAt;
    return {
        id: doc?.id ?? doc?._id ?? idx,
        name: doc?.fileName || doc?.name || `Document ${idx + 1}`,
        bg: isImage ? "#D8F0FF" : isWord ? "#F6FFD8" : "#FFDEDF",
        icon: isImage ? ImageIcon : isWord ? DocIcon : PdfIcon,
        // DOC.svg already includes its own 64px tile, so it's drawn at full tile size.
        iconFillsTile: isWord,
        meta: [sizeLabel, dateValue ? moment(dateValue).format("DD MMM YYYY") : ""].filter(Boolean).join(" | "),
        url: resolveDocumentUrl(doc?.s3Path),
    };
};

export const ghostButtonStyle = (color) => ({
    width: fluid(136),
    height: fluid(45),
    padding: `${fluid(13)} ${fluid(32)}`,
    boxSizing: "border-box",
    borderRadius: fluid(12),
    border: `1px solid ${color}`,
    backgroundColor: "transparent",
    color,
    fontFamily: "Roboto, sans-serif",
    fontSize: fluid(16),
    fontWeight: 700,
    lineHeight: "normal",
    letterSpacing: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
});

export const formatBatchDays = (days) => {
    if (!Array.isArray(days) || days.length === 0) return "";
    const dayMap = {
        "1": "Sun", "2": "Mon", "3": "Tue", "4": "Wed", "5": "Thu", "6": "Fri", "7": "Sat",
        1: "Sun", 2: "Mon", 3: "Tue", 4: "Wed", 5: "Thu", 6: "Fri", 7: "Sat",
    };
    const numDays = days.map(d => parseInt(d, 10)).filter(n => !isNaN(n)).sort((a, b) => a - b);
    if (numDays.length === 0) return days.join(", ");

    let isContiguous = numDays.length > 1;
    for (let i = 1; i < numDays.length; i++) {
        if (numDays[i] !== numDays[i - 1] + 1) {
            isContiguous = false;
            break;
        }
    }

    if (isContiguous) {
        return `${dayMap[numDays[0]] || numDays[0]}–${dayMap[numDays[numDays.length - 1]] || numDays[numDays.length - 1]}`;
    }
    return numDays.map(n => dayMap[n] || n).join(", ");
};

export const formatBatchTime = (timeStr) => {
    if (!timeStr) return "";
    const m = moment(timeStr, ["HH:mm:ss", "HH:mm", "h:mm A", "h:mm a"], true);
    if (m.isValid()) {
        return m.minutes() === 0 ? m.format("h a") : m.format("h:mm a");
    }
    return timeStr;
};

export const mapBatchRow = (b, bIdx = 0, courseProgress = 0) => {
    if (!b) return null;
    if (b.details && b.code && b.name && (!b.endDate || b.details.includes("Expires"))) return b;

    const id = b.standardId || b.id || bIdx;

    let name = b.name;
    if (!name) {
        if (b.std && b.section) {
            name = `${b.std} - ${b.section}`;
        } else if (b.std) {
            name = b.std;
        } else if (b.section) {
            name = `Section ${b.section}`;
        } else {
            name = `Batch ${bIdx + 1}`;
        }
        if (b.stream) {
            name += ` (${b.stream})`;
        }
    }

    let code = b.code;
    if (!code) {
        if (b.section) {
            // Tile shows just the section (e.g. "A", "B").
            code = String(b.section);
        } else if (b.std) {
            code = b.std.slice(0, 3).toUpperCase();
        } else {
            code = `B${bIdx + 1}`;
        }
    }

    const detailsParts = [];
    if (b.rollNo != null && b.rollNo !== "") {
        detailsParts.push(`Roll #${b.rollNo}`);
    }

    const daysFormatted = formatBatchDays(b.days);
    const startFormatted = formatBatchTime(b.startTime);
    const endFormatted = formatBatchTime(b.endTime);

    let scheduleText = "";
    if (startFormatted && endFormatted) {
        scheduleText = `${startFormatted} – ${endFormatted}`;
    } else if (startFormatted) {
        scheduleText = startFormatted;
    }

    if (daysFormatted && scheduleText) {
        detailsParts.push(`${daysFormatted}, ${scheduleText}`);
    } else if (daysFormatted) {
        detailsParts.push(daysFormatted);
    } else if (scheduleText) {
        detailsParts.push(scheduleText);
    }

    const rawEndDate = b.endDate || b.expiryDate;
    if (rawEndDate) {
        const parsed = moment(rawEndDate);
        const formattedDate = parsed.isValid() ? parsed.format("DD MMM YYYY") : String(rawEndDate);
        detailsParts.push(`Expires ${formattedDate}`);
    }

    const details = detailsParts.join(" · ") || b.details || "";
    const color = b.color || BATCH_COLORS[bIdx % BATCH_COLORS.length];
    const progress = b.completionPercent != null
        ? Number(b.completionPercent)
        : (courseProgress != null ? Number(courseProgress) : 0);

    return {
        id,
        code,
        color,
        name,
        details,
        progress,
        endDate: b.endDate || b.expiryDate,
        startDate: b.startDate,
        raw: b,
    };
};

const LiveCourseIconComponent = ({ size = 32 }) => (
    <img src={LiveCoursesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const TestSeriesIconComponent = ({ size = 32 }) => (
    <img src={TestSeriesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const VideoCourseIconComponent = ({ size = 32 }) => (
    <img src={VideoCourseIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const StudyMaterialsIconComponent = ({ size = 32 }) => (
    <img src={StudyMaterialsIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

export const mapStudentCourseRow = (c, idx) => {
    let icon = VideoCourseIconComponent;
    let iconBg = "#D8F0FF";
    let iconColor = "#008BE3";
    let badge = { label: "Classroom course", ...COURSE_BADGE_TOKENS.live };

    if (c.courseType === "videoResource") {
        icon = StudyMaterialsIconComponent;
        iconBg = "#DCF2B7";
        iconColor = "#609900";
        badge = { label: "Video course", ...COURSE_BADGE_TOKENS.videoCourse };
    } else if (c.courseType === "testSeries") {
        icon = TestSeriesIconComponent;
        iconBg = "#FFEFC5";
        iconColor = "#D97706";
        badge = { label: "Test series", ...COURSE_BADGE_TOKENS.testSeries };
    } else if (c.courseType === "studyMaterial") {
        icon = LiveCourseIconComponent;
        iconBg = "#FFDEDF";
        iconColor = "#DC2626";
        badge = { label: "Study material", ...COURSE_BADGE_TOKENS.studyMaterials };
    }

    const rawBatches = c.batches || c.standards || (c.batch ? [c.batch] : []);
    const courseProgress = c.completionPercent != null ? Number(c.completionPercent) : 0;
    const mappedBatches = Array.isArray(rawBatches)
        ? rawBatches.map((b, bIdx) => mapBatchRow(b, bIdx, courseProgress)).filter(Boolean)
        : [];

    return {
        id: c.enrollmentId || c.id || c._id || idx,
        enrollmentId: c.enrollmentId || c.id || c._id,
        courseId: c.courseId || c.course?.id || c.course?._id || c.id || c._id,
        courseType: c.courseType || c.type || "course",
        icon, iconBg, iconColor,
        title: c.courseName || c.title || c.course?.name || "Unknown Course",
        enrolledOn: c.startDate ? moment(c.startDate).format("DD MMM YYYY") : "N/A",
        expiresOn: c.endDate ? moment(c.endDate).format("DD MMM YYYY") : (c.expiryDate ? moment(c.expiryDate).format("DD MMM YYYY") : "N/A"),
        badge,
        progress: courseProgress,
        batches: mappedBatches,
    };
};

export const formatTotalPaid = (amount) => {
    if (amount == null || amount === '') return '₹0';
    const num = Number(amount);
    if (isNaN(num)) return String(amount).startsWith('₹') ? String(amount) : `₹${amount}`;
    if (num === 0) return '₹0';
    if (num >= 100000) {
        const lac = num / 100000;
        return `₹${Number.isInteger(lac) ? lac : lac.toFixed(1)}L`;
    }
    if (num >= 1000) {
        const k = num / 1000;
        return `₹${Number.isInteger(k) ? k : k.toFixed(1)}k`;
    }
    return `₹${num.toLocaleString('en-IN')}`;
};

export const getAttendanceBgColor = (status) => {
    if (!status) return "transparent";
    const s = String(status).toLowerCase().replace(/[\s_-]/g, "");
    if (s === "present") return "#C3F3D2";
    if (s === "absent") return "#FFD7D8";
    if (s === "partiallypresent" || s === "partial" || s === "halfday") return "#FEE7A8";
    if (s === "noclass") return "#F1F1FF";
    if (s === "holiday") return "#E2C3F3";
    if (s === "leave" || s === "onleave") return "#C3E9F3";
    if (s === "cancelled" || s === "classcancelled" || s === "offday" || s === "weeklyoff") return "#F2F2F2";
    return "transparent";
};

export const getStatusBadgeStyle = (status) => {
    const s = String(status || "").toLowerCase().replace(/[\s_-]/g, "");
    if (s === "present") {
        return { bg: "#ECFDF5", stroke: "#34D399", text: "#059669" };
    }
    if (s === "absent") {
        return { bg: "#FEF2F2", stroke: "#F87171", text: "#DC2626" };
    }
    if (s === "partiallypresent" || s === "partial") {
        return { bg: "#FFFBEB", stroke: "#FBBF24", text: "#D97706" };
    }
    if (s === "offday" || s === "weeklyoff" || s === "cancelled" || s === "classcancelled") {
        return { bg: "#F3F4F6", stroke: "#D1D5DB", text: "#6B7280" };
    }
    return { bg: "#F3F4F6", stroke: "#D1D5DB", text: "#6B7280" };
};

export const formatTimeRange = (start, end) => {
    if (!start && !end) return "";
    const formatTime = (t) => {
        if (!t) return "";
        if (t.includes("AM") || t.includes("PM")) return t;
        const m = moment(t, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]);
        return m.isValid() ? m.format("hh:mm A") : t;
    };
    if (start && end) return `${formatTime(start)} - ${formatTime(end)}`;
    return formatTime(start || end);
};

export const formatClockTime = (t) => {
    if (!t) return "--:--";
    const m = moment(t, ["HH:mm:ss", "HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "h:mm A", "hh:mm A"]);
    if (m.isValid()) {
        return m.format("h:mm");
    }
    return String(t);
};

export const generateCalendarMonth = (year, monthIndex, seedOffset) => {
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const firstWeekday = (new Date(year, monthIndex, 1).getDay() + 6) % 7; // Mon=0..Sun=6
    const statusCycle = ['present', 'present', 'present', 'present', 'partial', 'present', 'cancelled', 'present', 'absent', 'present', 'present', 'cancelled'];
    const days = Array.from({ length: firstWeekday }, () => ({ empty: true }));
    for (let day = 1; day <= daysInMonth; day++) {
        days.push({ day, status: statusCycle[(day + seedOffset) % statusCycle.length] });
    }
    return days;
};
